// "Want it today?" - checks a postcode for same-day delivery.
// Postcode -> location: postcodes.io. Distance is a straight line, matching Shopify's local delivery radius.
// Rates/areas/cut-off live in the config block of sections/pickup-availability.liquid.
if (!customElements.get('same-day-delivery')) {
  customElements.define(
    'same-day-delivery',
    class SameDayDelivery extends HTMLElement {
      connectedCallback() {
        // in the cart drawer the stores are worked out by cart-delivery-estimate, which calls init()
        if (this.hasAttribute('data-defer')) {
          this.hidden = true;
          return;
        }
        this.init();
      }

      init() {
        this.hidden = false;
        this.config = JSON.parse(this.dataset.config);
        const stocked = (this.dataset.stores || '').split(',').filter(Boolean);
        this.stores = this.config.stores.filter((s) => stocked.some((n) => n.includes(s.match)));
        this.input = this.querySelector('.same-day__input');
        this.result = this.querySelector('.same-day__result');

        if (!this.stores.length) {
          this.hidden = true;
          return;
        }

        this.querySelector('form').addEventListener('submit', (e) => {
          e.preventDefault();
          this.check(this.input.value);
        });

        // product page: quantity/variant changes move the basket value
        document.addEventListener('th:basket', () => {
          if (this.isConnected && this.lastMiles != null) this.show(this.lastMiles);
        });

        let saved = '';
        try { saved = localStorage.getItem('th_postcode') || ''; } catch (e) {}
        if (saved) {
          this.input.value = saved;
          this.check(saved);
        }
      }

      normalise(raw) {
        const pc = String(raw || '').toUpperCase().replace(/\s+/g, '');
        return /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/.test(pc) ? pc : null;
      }

      say(html, tone) {
        this.result.className = 'same-day__result' + (tone ? ' same-day__result--' + tone : '');
        this.result.innerHTML = html;
      }

      async check(raw) {
        const pc = this.normalise(raw);
        if (!pc) return this.say('Enter a full UK postcode, like TQ14 8AF.', 'warn');
        this.say('Checking…');
        try {
          const loc = await this.locate(pc);
          if (!loc) return this.say("We couldn't find that postcode. Check it and try again.", 'warn');
          const miles = this.nearestMiles(loc);
          try { localStorage.setItem('th_postcode', pc); } catch (e) {}
          this.show(miles);
        } catch (e) {
          this.say("We couldn't check that just now. Try again in a moment.", 'warn');
        }
      }

      async locate(pc) {
        const key = 'th_pc_' + pc;
        try {
          const hit = sessionStorage.getItem(key);
          if (hit) return JSON.parse(hit);
        } catch (e) {}
        const res = await fetch('https://api.postcodes.io/postcodes/' + encodeURIComponent(pc));
        if (res.status === 404) return null;
        const json = await res.json();
        if (!json.result) return null;
        const loc = { lat: json.result.latitude, lng: json.result.longitude };
        try { sessionStorage.setItem(key, JSON.stringify(loc)); } catch (e) {}
        return loc;
      }

      straightMiles(a, b) {
        const R = 3958.8, rad = (d) => (d * Math.PI) / 180;
        const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
        const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
        return 2 * R * Math.asin(Math.sqrt(h));
      }

      // Straight-line distance, to match Shopify's local delivery radius.
      nearestMiles(loc) {
        return Math.min(...this.stores.map((s) => this.straightMiles(s, loc)));
      }

      londonNow() {
        const parts = new Intl.DateTimeFormat('en-GB', {
          timeZone: 'Europe/London', weekday: 'short', hour: 'numeric', minute: 'numeric', hourCycle: 'h23',
        }).formatToParts(new Date());
        const get = (t) => parts.find((p) => p.type === t).value;
        const day = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }[get('weekday')];
        return { day, minutes: parseInt(get('hour'), 10) * 60 + parseInt(get('minute'), 10) };
      }

      nextOpenLabel(today) {
        const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        for (let i = 1; i <= 7; i++) {
          const d = (today + i) % 7;
          if (this.config.open_days.includes(d)) return i === 1 ? 'tomorrow' : names[d];
        }
        return 'tomorrow';
      }

      // Basket value in pence: whole cart in the drawer, this item x quantity on the product page.
      basketPence() {
        const cart = this.closest('cart-delivery-estimate');
        if (cart) return parseInt(cart.dataset.total, 10) || 0;
        return typeof window.thBasketPence === 'function' ? window.thBasketPence() : 0;
      }

      show(miles) {
        this.lastMiles = miles;
        const c = this.config;
        const price = (n) => '£' + (Number.isInteger(Number(n)) ? Number(n) : Number(n).toFixed(2));
        const note = c.checkout_note ? `<span class="same-day__note">${c.checkout_note}</span>` : '';
        if (miles > c.miles) {
          const where = this.stores.map((s) => s.name).join(' or ');
          return this.say(
            `Sorry, same-day delivery covers up to ${c.miles} miles from ${where}. Standard delivery or free click &amp; collect are still available.`,
            'warn'
          );
        }

        // free local delivery threshold, worked out like the standard / next day delivery cards
        const basket = this.basketPence();
        const threshold = Math.round(c.free_over * 100);
        const free = c.free_over && basket >= threshold;
        const fee = free ? `<s>${price(c.fee)}</s> <span class="same-day__free">FREE</span>` : price(c.fee);
        let nudge = '';
        if (c.free_over) {
          nudge = free
            ? '<span class="same-day__nudge">✓ You qualify for free local delivery</span>'
            : `<span class="same-day__nudge">Or spend <strong>${price((threshold - basket) / 100)}</strong> more for <strong>free</strong> local delivery</span>`;
        }

        const now = this.londonNow();
        const left = c.cutoff_hour * 60 - now.minutes;
        const openToday = c.open_days.includes(now.day);
        if (openToday && left > 0) {
          const h = Math.floor(left / 60), m = left % 60;
          const time = (h ? h + 'h ' : '') + m + 'm';
          return this.say(
            `<strong>Yes, we can deliver today for ${fee}.</strong> Order within ${time} and it arrives by ${c.deliver_by}.` + nudge + note,
            'ok'
          );
        }
        const when = this.nextOpenLabel(now.day);
        this.say(
          `<strong>Order now, local delivery for ${fee}, before ${c.next_day_by} ${when}.</strong>` + nudge + note,
          'ok'
        );
      }
    }
  );
}
